/**
 * Region Context Provider
 * 
 * This context provides region-specific functionality including:
 * - Currency formatting
 * - Date formatting
 * - Financial year calculations
 * - Tax calculations
 * - Localization settings
 * 
 * Features:
 * - Region Pack integration
 * - Australian-first implementation
 * - Extensible for future regions (NZ)
 * - Constitution compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { RegionPack } from '../lib/regions/au';
import { usePreferences } from '../hooks/usePreferences';
import type { UserPreferences } from '../types/preference.types';
import { formatCurrencyWithPreferences } from '../utils/currency-formatter';
import { formatDateWithPreferences, parseDateWithPreferences } from '../utils/date-formatter';

interface RegionContextType {
  regionPack: RegionPack;
  formatCurrency: (amount: number, options?: {
    showSymbol?: boolean;
    showDecimals?: boolean;
    absolute?: boolean;
  }) => string;
  formatDate: (date: Date | string, format?: 'display' | 'input' | 'parse') => string;
  parseDate: (dateString: string) => Date | null;
  isInFinancialYear: (date: Date | string, fyStartYear: number) => boolean;
  getFinancialYear: (date: Date | string) => number;
  calculateGST: (amount: number, isGSTInclusive?: boolean) => number;
  formatGST: (amount: number, isGSTInclusive?: boolean) => string;
  formatTime: (date: Date | string, options?: {
    includeSeconds?: boolean;
    use24Hour?: boolean;
  }) => string;
  validateBankAccount: (accountNumber: string) => boolean;
  formatBankAccount: (accountNumber: string) => string;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

interface RegionProviderProps {
  children: ReactNode;
  regionPack: RegionPack;
}

/**
 * Region Provider Component
 * 
 * Provides region-specific functionality to the application.
 */
export function RegionProvider({ children, regionPack }: RegionProviderProps): React.ReactElement {
  const { data: preferences } = usePreferences();
  
  // Import utility functions from the region pack
  const {
    formatCurrency: regionFormatCurrency,
    formatDate: regionFormatDate,
    parseDate: regionParseDate,
    isInFinancialYear,
    getFinancialYear,
    calculateGST,
    formatGST,
    formatTime,
    validateBankAccount,
    formatBankAccount,
  } = regionPack;

  // Wrap formatCurrency to use user preferences
  const formatCurrency = useMemo(() => {
    return (amount: number, options?: {
      showSymbol?: boolean;
      showDecimals?: boolean;
      absolute?: boolean;
    }) => {
      // Use preference-aware formatter if preferences are available
      if (preferences) {
        return formatCurrencyWithPreferences(amount, preferences as UserPreferences, options);
      }
      // Fallback to region pack formatter
      return regionFormatCurrency(amount, options);
    };
  }, [preferences, regionFormatCurrency]);

  // Wrap formatDate to use user preferences
  const formatDate = useMemo(() => {
    return (date: Date | string, formatType?: 'display' | 'input' | 'parse') => {
      // For input format, always return YYYY-MM-DD (HTML date input format)
      if (formatType === 'input') {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        if (isNaN(dateObj.getTime())) return '';
        return dateObj.toISOString().split('T')[0];
      }
      
      // Use preference-aware formatter if preferences are available
      if (preferences) {
        return formatDateWithPreferences(date, preferences as UserPreferences);
      }
      // Fallback to region pack formatter
      return regionFormatDate(date, formatType);
    };
  }, [preferences, regionFormatDate]);

  // Wrap parseDate to use user preferences
  const parseDate = useMemo(() => {
    return (dateString: string): Date | null => {
      // Use preference-aware parser if preferences are available
      if (preferences) {
        return parseDateWithPreferences(dateString, preferences as UserPreferences);
      }
      // Fallback to region pack parser
      return regionParseDate(dateString);
    };
  }, [preferences, regionParseDate]);

  const value: RegionContextType = {
    regionPack,
    formatCurrency,
    formatDate,
    parseDate,
    isInFinancialYear,
    getFinancialYear,
    calculateGST,
    formatGST,
    formatTime,
    validateBankAccount,
    formatBankAccount,
  };

  return (
    <RegionContext.Provider value={value}>
      {children}
    </RegionContext.Provider>
  );
}

/**
 * Hook to use region context
 */
export function useRegion(): RegionContextType {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
}
